import { getGPUTier } from "detect-gpu";
import { detectIncognito } from "detectincognitojs";
import type { SetupAttributeType } from "../types";
import type {
  PersonaPrimaryAttributes,
  PersonaSecondaryAttributes,
} from "./types";
import { getUaParserWithAppleDevicesModels, getUserDeviceInfo } from "./utils";

export function prepare() {
  if (!window.helium) {
    window.helium = {} as any;
  }

  window.helium.attributes = {
    ...(window?.helium?.attributes || {}),
    persona: {
      ...(window?.helium?.attributes?.persona || {}),
      primary: {
        ...(window?.helium?.attributes?.persona?.primary || {}),
      },
      secondary: {
        ...(window?.helium?.attributes?.persona?.secondary || {}),
      },
    },
  };
}

export const personaPrimaryAttributesSetup: SetupAttributeType<PersonaPrimaryAttributes> =
  [
    {
      id: "isp",
      setup: () => {
        // implemented in "location"
      },
    },
    // {
    //   id: "location",
    //   setup: () => {
    //     getUserDeviceInfo()
    //       .then((info) => {
    //         window.helium.attributes.persona.primary.location = {
    //           country: info.country,
    //           city: info.city,
    //           geolocationEnabled: false,
    //         };

    //         window.helium.attributes.persona.primary.isp = info.isp;
    //         window.helium.attributes.persona.primary.countryName = info.country;

    //         window.helium.attributes.persona.secondary.weatherCondition =
    //           info.weather;
    //       })
    //       .catch(() => {
    //         window.helium.attributes.persona.primary.location = {
    //           geolocationEnabled: false,
    //           country: "",
    //         };

    //         window.helium.attributes.persona.primary.isp = "Unknown";
    //         window.helium.attributes.persona.primary.countryName = "Unknown";
    //         window.helium.attributes.persona.secondary.weatherCondition =
    //           "Unknown";
    //       });

    //     return window.helium.attributes.persona.primary.location;
    //   },
    // },
    {
      id: "device",
      setup: () => {
        // implemented in GPU family
      },
    },
    {
      id: "language",
      setup: () => {
        window.helium.attributes.persona.primary.language = {
          primaryLanguage: navigator.language,
          secondaryLanguages: navigator.languages
            ? navigator.languages.slice(1)
            : [],
        };
        return window.helium.attributes.persona.primary.language;
      },
    },
    {
      id: "battery",
      setup: () => {
        if (navigator.getBattery) {
          navigator.getBattery().then((battery) => {
            window.helium.attributes.persona.primary.battery = {
              level: battery.level * 100,
              charging: battery.charging,
            };

            window.helium.attributes.persona.primary.batteryCategory =
              battery.level * 100 >= 60
                ? "high"
                : battery.level * 100 < 60
                  ? "medium"
                  : "low";
          });
        }
        return window.helium.attributes.persona.primary.battery;
      },
    },
    {
      id: "network",
      setup: () => {
        const connection =
          navigator.connection ||
          navigator.mozConnection ||
          navigator.webkitConnection;
        if (connection) {
          window.helium.attributes.persona.primary.network = {
            connectionType: connection.type,
            effectiveType: connection.effectiveType,
          };
        }
        return window.helium.attributes.persona.primary.network;
      },
    },
    {
      id: "timeZone",
      setup: () => {
        window.helium.attributes.persona.primary.timeZone =
          Intl.DateTimeFormat().resolvedOptions().timeZone;
        return window.helium.attributes.persona.primary.timeZone;
      },
    },
    {
      id: "platform",
      setup: () => {
        window.helium.attributes.persona.primary.platform = "web"; // Assuming this is a web setup, modify if needed
        return window.helium.attributes.persona.primary.platform;
      },
    },
    {
      id: "viewport",
      setup: () => {
        window.helium.attributes.persona.primary.viewport = {
          width: window.innerWidth,
          height: window.innerHeight,
        };
        return window.helium.attributes.persona.primary.viewport;
      },
    },

    {
      id: "cookieEnabled",
      setup: () => {
        window.helium.attributes.persona.primary.cookieEnabled =
          navigator.cookieEnabled;
        return window.helium.attributes.persona.primary.cookieEnabled;
      },
    },
    {
      id: "javascriptEnabled",
      setup: () => {
        window.helium.attributes.persona.primary.javascriptEnabled = true; // If this code runs, JavaScript is enabled
        return window.helium.attributes.persona.primary.javascriptEnabled;
      },
    },
    {
      id: "adBlockerDetected",
      setup: () => {
        let adBlockDetected = false;
        // 3. Test loading a common ad network script and check if blocked
        const testScript = document.createElement("script");
        testScript.src =
          "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js";
        testScript.onerror = () => {
          window.helium.attributes.persona.primary.adBlockerDetected = true;
          adBlockDetected = true; // If script fails to load, assume ad blocker is active
        };
        document.head.appendChild(testScript);

        // 4. Check if common ad blocker-specific elements are present
        if (window.blockAdBlock || window.AdBlock || window.FuckAdBlock) {
          adBlockDetected = true; // Popular ad blocker detection libraries
        }
        console.log({ adBlockDetected });
        // Store the result in the persona object
        window.helium.attributes.persona.primary.adBlockerDetected =
          adBlockDetected;

        return window.helium.attributes.persona.primary.adBlockerDetected;
      },
    },

    {
      id: "multiTabUsageDetected",
      setup: () => {
        // TODO
        // Track if the user opens multiple tabs
        let multiTabDetected = false;
        window.addEventListener("storage", (event) => {
          if (event.key === "multiTabCheck") {
            multiTabDetected = true;
          }
        });
        localStorage.setItem("multiTabCheck", String(Date.now()));
        window.helium.attributes.persona.primary.multiTabUsageDetected =
          multiTabDetected;
        return window.helium.attributes.persona.primary.multiTabUsageDetected;
      },
    },
    {
      id: "preferredCurrency",
      setup: () => {
        // Check if Shopify has an active currency set (available in multi-currency stores)
        if (
          window.Shopify &&
          window.Shopify.currency &&
          window.Shopify.currency.active
        ) {
          window.helium.attributes.persona.primary.preferredCurrency =
            window.Shopify.currency.active;
        } else {
          // Fallback to geolocation or browser settings for currency inference
          const language = navigator.language;
          let currency = "USD"; // Default currency fallback

          // Currency inference based on browser language (simple mapping)
          if (language.includes("en-GB")) {
            currency = "GBP"; // British Pounds
          } else if (language.includes("en-CA") || language.includes("fr-CA")) {
            currency = "CAD"; // Canadian Dollars
          } else if (language.includes("en-AU")) {
            currency = "AUD"; // Australian Dollars
          } else if (language.includes("fr-FR")) {
            currency = "EUR"; // Euros for European markets
          } else if (language.includes("zh-CN")) {
            currency = "CNY"; // Chinese Yuan
          }

          window.helium.attributes.persona.primary.preferredCurrency = currency;
        }

        return window.helium.attributes.persona.primary.preferredCurrency;
      },
    },

    {
      id: "regionalSettings",
      setup: () => {
        const locale = navigator.language;
        const dateFormat = locale.includes("US") ? "MM/DD/YYYY" : "DD/MM/YYYY";
        const numberFormat = locale.includes("US") ? "1,000.00" : "1.000,00";
        window.helium.attributes.persona.primary.regionalSettings = {
          dateFormat,
          numberFormat,
        };
        return window.helium.attributes.persona.primary.regionalSettings;
      },
    },
    {
      id: "VPNDetected",
      setup: () => {
        const vpnDetected = false;

        // 1. Attempt WebRTC-based detection to compare IP addresses
        // const detectWebRTCIP = () => {
        //   const peerConnection = new RTCPeerConnection({ iceServers: [] });
        //   peerConnection.createDataChannel("");
        //   peerConnection
        //     .createOffer()
        //     .then((offer) => peerConnection.setLocalDescription(offer));
        //   peerConnection.onicecandidate = (event) => {
        //     if (event && event.candidate && event.candidate.candidate) {
        //       const ipRegex = /([0-9]{1,3}(\.[0-9]{1,3}){3})/;
        //       const ipMatch = event.candidate.candidate.match(ipRegex);
        //       console.log(event.candidate.candidate);
        //       if (ipMatch) {
        //         const webRTCLocalIP = ipMatch[1];

        //         console.log(webRTCLocalIP);
        //         // Compare WebRTC local IP to public IP (requires external API for public IP)
        //         // TODO: Add logic to compare with public IP from an external service
        //       }
        //     }
        //   };
        // };

        // Run detection strategies
        // detectWebRTCIP();

        // Set the result
        window.helium.attributes.persona.primary.VPNDetected = vpnDetected;
        return window.helium.attributes.persona.primary.VPNDetected;
      },
    },
    {
      id: "incognitoMode",
      setup: () => {
        window.helium.attributes.persona.primary.incognitoMode = false; // Default false

        detectIncognito().then((result) => {
          window.helium.attributes.persona.primary.incognitoMode =
            result.isPrivate;
        });

        return window.helium.attributes.persona.primary.incognitoMode;
      },
    },
    {
      id: "mediaAutoplayBlocked",
      setup: () => {
        // Test for media autoplay blocking by creating a video element
        const video = document.createElement("video");
        video.autoplay = true;
        video.muted = true;
        window.helium.attributes.persona.primary.mediaAutoplayBlocked =
          !video.autoplay;

        video.remove();
        return window.helium.attributes.persona.primary.mediaAutoplayBlocked;
      },
    },
    {
      id: "webPImageSupport",
      setup: () => {
        // Test for WebP support using a data URI
        const img = new Image();
        img.onload = img.onerror = () => {
          window.helium.attributes.persona.primary.webPImageSupport =
            img.width === 1;
        };
        img.src = "data:image/webp;base64,UklGRhYAAABXRUJQVlA4TBEAAAAvAAAAAA";
        return window.helium.attributes.persona.primary.webPImageSupport;
      },
    },
    {
      id: "GPUFamily",
      setup: () => {
        getGPUTier().then((gpu) => {
          console.log({ gpu });
          window.helium.attributes.persona.primary.GPUFamily = gpu.gpu;
          window.helium.attributes.persona.primary.GPUTier = gpu.tier;

          getUaParserWithAppleDevicesModels().then((parsedUa) => {
            // if (deviceModel !== "unknown") {
            //   const attributes = getDeviceTierAndRecency(deviceModel as string);

            //   if (attributes) {
            //     const { recency, tier, full_name } = attributes;

            //     window.helium.attributes.persona.primary.deviceModelTier =
            //       tier + "";
            //     window.helium.attributes.persona.primary.deviceModelRecency =
            //       recency + "";

            //     window.helium.attributes.persona.primary.deviceFullName =
            //       full_name;
            //   }
            // }

            window.helium.attributes.persona.primary = {
              ...window.helium.attributes.persona.primary,
              ...(parsedUa as any),
              screenResolution: {
                width: window.screen.width,
                height: window.screen.height,
              },
            };
          });
        });
      },
    },
    {
      id: "location",
      setup: () => {
        if (
          window.location.search.includes("city") ||
          window.location.search.includes("country")
        ) {
          window.helium.attributes.persona.primary.location = {
            country:
              new URL(window.location.href).searchParams.get("country") || "",
            city: new URL(window.location.href).searchParams.get("city") || "",
            geolocationEnabled: false,
          };
        }
        console.log("GETTING USER DEVICE INFO");

        function setUserDeviceInfo(info: any) {
          if (
            !window.location.search.includes("city") ||
            !window.location.search.includes("country")
          ) {
            window.helium.attributes.persona.primary.location = {
              country: info.country,
              city: info.city,
              geolocationEnabled: false,
            };
            window.helium.attributes.persona.secondary.cityTier = info.cityTier;
            window.helium.attributes.persona.primary.countryName = info.country;
          }

          window.helium.attributes.persona.primary.isp = info.isp;

          window.helium.attributes.persona.secondary.weatherCondition =
            info.weather;

          if (info.deviceModel) {
            const { recency, tier, full_name } = info.deviceModel;

            window.helium.attributes.persona.primary.deviceModelTier =
              tier + "";
            window.helium.attributes.persona.primary.deviceModelRecency =
              recency + "";

            window.helium.attributes.persona.primary.deviceFullName = full_name;
          }
        }

        function retry() {
          const interval = setTimeout(() => {
            getUserDeviceInfo().then((info) => {
              setUserDeviceInfo(info);
              clearInterval(interval);
            });
          }, 200);
        }

        getUserDeviceInfo()
          .then((info) => {
            console.log("Got user device info ", { info });
            if (info) {
              setUserDeviceInfo(info);

              return;
            }

            console.log("No user device info found, retrying...");

            retry();
          })
          .catch((e) => {
            console.log(
              "Error getting user device info, setting default values",
              e,
            );

            window.helium.attributes.persona.primary.location = {
              geolocationEnabled: false,
              country: "",
            };

            window.helium.attributes.persona.primary.isp = "Unknown";
            window.helium.attributes.persona.primary.countryName = "Unknown";
            window.helium.attributes.persona.secondary.weatherCondition =
              "Unknown";

            retry();
          });

        return window.helium.attributes.persona.primary.location;
      },
    },
    {
      id: "sessionReferrerDomain",
      setup: () => {
        const referrerDomain = document.referrer
          ? new URL(document.referrer).hostname
          : "";
        window.helium.attributes.persona.primary.sessionReferrerDomain =
          referrerDomain;
        return window.helium.attributes.persona.primary.sessionReferrerDomain;
      },
    },
    {
      id: "sessionUTMParameters",

      setup: () => {
        const urlParams = new URLSearchParams(window.location.search);
        const utmParams = Object.fromEntries(
          Array.from(urlParams.entries()).filter(([a]) => a.includes("utm")),
        );

        window.helium.attributes.persona.primary.sessionUTMParameters =
          utmParams;
        return window.helium.attributes.persona.primary.sessionUTMParameters;
      },
    },
    {
      id: "sessionDeviceRotation",
      setup: () => {
        window.helium.attributes.persona.primary.sessionDeviceRotation =
          "Portrait";
        window.addEventListener("orientationchange", () => {
          const orientation =
            screen.orientation || screen.mozOrientation || screen.msOrientation;
          window.helium.attributes.persona.primary.sessionDeviceRotation =
            orientation.angle === 0 ? "Portrait" : "Landscape";
        });

        return window.helium.attributes.persona.primary.sessionDeviceRotation;
      },
    },
    {
      id: "touchScreen",
      setup: () => {
        window.helium.attributes.persona.primary.touchScreen =
          "ontouchstart" in window || navigator.maxTouchPoints > 0;
        return window.helium.attributes.persona.primary.touchScreen;
      },
    },
    {
      id: "serviceWorkerEnabled",
      setup: () => {
        window.helium.attributes.persona.primary.serviceWorkerEnabled =
          !!navigator.serviceWorker;
        return window.helium.attributes.persona.primary.serviceWorkerEnabled;
      },
    },
    {
      id: "secureConnection",
      setup: () => {
        window.helium.attributes.persona.primary.secureConnection =
          window.location.protocol === "https:";
        return window.helium.attributes.persona.primary.secureConnection;
      },
    },
    {
      id: "textScalingEnabled",
      setup: () => {
        const textScaling = window.devicePixelRatio > 1;
        window.helium.attributes.persona.primary.textScalingEnabled =
          textScaling;
        return window.helium.attributes.persona.primary.textScalingEnabled;
      },
    },
    {
      id: "darkModeEnabled",
      setup: () => {
        const darkMode =
          window.matchMedia &&
          window.matchMedia("(prefers-color-scheme: dark)").matches;
        window.helium.attributes.persona.primary.darkModeEnabled = darkMode;
        return window.helium.attributes.persona.primary.darkModeEnabled;
      },
    },
    {
      id: "voiceAssistantEnabled",
      setup: () => {
        const isVoiceAssistantEnabled = /Siri|Google Assistant|Alexa/.test(
          navigator.userAgent,
        );
        window.helium.attributes.persona.primary.voiceAssistantEnabled =
          isVoiceAssistantEnabled;
        return window.helium.attributes.persona.primary.voiceAssistantEnabled;
      },
    },
    {
      id: "firstVisit",
      setup: () => {
        const firstVisit = !localStorage.getItem("visitedBefore");
        if (firstVisit) localStorage.setItem("visitedBefore", Date.now() + "");
        window.helium.attributes.persona.primary.firstVisit = firstVisit;
        return window.helium.attributes.persona.primary.firstVisit;
      },
    },
  ];

export const personaSecondaryAttributesSetup: SetupAttributeType<PersonaSecondaryAttributes> =
  [
    {
      id: "cityTier",
      setup: () => {
        // window.helium.attributes.persona.secondary.cityTier = ("Tier " +
        //   (getLocationTier() || "2")) as `Tier ${1 | 2 | 3}`;
        // return window.helium.attributes.persona.secondary.cityTier;
      },
    },

    {
      id: "weatherCondition",
      setup: () => {
        // implemented in primary array for now under "location"
      },
    },
    {
      id: "localTimeCategory",
      setup: () => {
        const hours = new Date().getHours();
        if (hours >= 6 && hours < 12) {
          window.helium.attributes.persona.secondary.localTimeCategory =
            "Morning";
        } else if (hours >= 12 && hours < 18) {
          window.helium.attributes.persona.secondary.localTimeCategory =
            "Afternoon";
        } else if (hours >= 18 && hours < 24) {
          window.helium.attributes.persona.secondary.localTimeCategory =
            "Evening";
        } else {
          window.helium.attributes.persona.secondary.localTimeCategory =
            "Night";
        }
        return window.helium.attributes.persona.secondary.localTimeCategory;
      },
    },

    {
      id: "internetSpeedCategory",
      setup: () => {
        const connection =
          navigator.connection ||
          navigator.mozConnection ||
          navigator.webkitConnection;
        if (connection) {
          if (connection.downlink > 5) {
            window.helium.attributes.persona.secondary.internetSpeedCategory =
              "High";
          } else if (connection.downlink > 2) {
            window.helium.attributes.persona.secondary.internetSpeedCategory =
              "Moderate";
          } else {
            window.helium.attributes.persona.secondary.internetSpeedCategory =
              "Low";
          }
        }
        return window.helium.attributes.persona.secondary.internetSpeedCategory;
      },
    },
    {
      id: "batteryLevelCategory",
      setup: () => {
        const batteryLevel =
          window.helium.attributes.persona.primary.battery?.level;
        if (batteryLevel !== undefined) {
          if (batteryLevel > 80) {
            window.helium.attributes.persona.secondary.batteryLevelCategory =
              "High";
          } else if (batteryLevel > 50) {
            window.helium.attributes.persona.secondary.batteryLevelCategory =
              "Medium";
          } else {
            window.helium.attributes.persona.secondary.batteryLevelCategory =
              "Low";
          }
        }
        return window.helium.attributes.persona.secondary.batteryLevelCategory;
      },
    },

    // {
    //   id: "hoverBehavior",
    //   setup: () => {
    //     let hoverCount = 0;
    //     document.body.addEventListener("mouseover", () => {
    //       hoverCount += 1;
    //       if (window.helium.attributes.persona.secondary.temp) {
    //         window.helium.attributes.persona.secondary.temp.hoverBehavior =
    //           hoverCount;
    //       } else {
    //         window.helium.attributes.persona.secondary.temp = {
    //           hoverBehavior: hoverCount,
    //         };
    //       }

    //       if (hoverCount > 10) {
    //         window.helium.attributes.persona.secondary.hoverBehavior =
    //           "Hover-heavy";
    //       } else if (hoverCount > 0) {
    //         window.helium.attributes.persona.secondary.hoverBehavior =
    //           "Hover-light";
    //       }
    //     });
    //     if (hoverCount === 0) {
    //       window.helium.attributes.persona.secondary.hoverBehavior = "None";
    //     }
    //     return window.helium.attributes.persona.secondary.hoverBehavior;
    //   },
    // },
    {
      id: "screenOrientation",
      setup: () => {
        const orientation = screen.orientation?.type.includes("portrait")
          ? "Portrait"
          : "Landscape";
        window.helium.attributes.persona.secondary.screenOrientation =
          orientation;
        return window.helium.attributes.persona.secondary.screenOrientation;
      },
    },
    {
      id: "screenResolutionCategory",
      setup: () => {
        const { width, height } = window.helium.attributes.persona.primary
          .screenResolution || { width: 0, height: 0 };
        const resolution = width * height;
        if (resolution >= 1920 * 1080) {
          window.helium.attributes.persona.secondary.screenResolutionCategory =
            "High";
        } else if (resolution >= 1280 * 720) {
          window.helium.attributes.persona.secondary.screenResolutionCategory =
            "Moderate";
        } else {
          window.helium.attributes.persona.secondary.screenResolutionCategory =
            "Low";
        }
        return window.helium.attributes.persona.secondary
          .screenResolutionCategory;
      },
    },
    {
      id: "referrerType",
      setup: () => {
        const referrer = document.referrer;
        let referrerType: typeof window.helium.attributes.persona.secondary.referrerType =
          "Direct";
        if (referrer.includes("google.com")) {
          referrerType = "Search Engine";
        } else if (
          referrer.includes("facebook.com") ||
          referrer.includes("twitter.com") ||
          referrer.includes("instagram.com")
        ) {
          referrerType = "Social Media";
        }

        window.helium.attributes.persona.secondary.referrerType = referrerType;
        return window.helium.attributes.persona.secondary.referrerType;
      },
    },
    {
      id: "sessionLengthEstimate",
      setup: () => {
        const sessionStart = Date.now();
        window.helium.attributes.persona.secondary.sessionLengthEstimate =
          "Short";

        setInterval(() => {
          const elapsedTime = (Date.now() - sessionStart) / 60;
          if (elapsedTime > 3) {
            window.helium.attributes.persona.secondary.sessionLengthEstimate =
              "Long";
          } else if (elapsedTime > 1) {
            window.helium.attributes.persona.secondary.sessionLengthEstimate =
              "Moderate";
          } else {
            window.helium.attributes.persona.secondary.sessionLengthEstimate =
              "Short";
          }
        }, 5000);

        return window.helium.attributes.persona.secondary.sessionLengthEstimate;
      },
    },
    {
      id: "screenPixelDensity",
      setup: () => {
        const pixelDensity = window.devicePixelRatio;
        if (pixelDensity > 2) {
          window.helium.attributes.persona.secondary.screenPixelDensity =
            "High";
        } else if (pixelDensity > 1) {
          window.helium.attributes.persona.secondary.screenPixelDensity =
            "Medium";
        } else {
          window.helium.attributes.persona.secondary.screenPixelDensity = "Low";
        }
        return window.helium.attributes.persona.secondary.screenPixelDensity;
      },
    },
    {
      id: "connectionStability",
      setup: () => {
        const connection =
          navigator.connection ||
          navigator.mozConnection ||
          navigator.webkitConnection;
        let stability = "Stable";
        if (connection && connection.rtt > 300) {
          stability = "Unstable";
        }
        window.helium.attributes.persona.secondary.connectionStability =
          stability;
        return window.helium.attributes.persona.secondary.connectionStability;
      },
    },
    {
      id: "osUpdateFrequency",
      setup: () => {
        // Assuming the OS name and version are stored in primary attributes
        const osName = window.helium.attributes.persona.primary.os?.name || "";
        const osVersion =
          window.helium.attributes.persona.primary.os?.version
            ?.split(".")
            ?.at(0) || "";

        // Simulated latest OS versions (this should ideally be dynamically fetched from an API)
        const latestOSVersions = {
          iOS: 17,
          Android: 14,
          macOS: 15,
        };

        let osUpdateFrequency: typeof window.helium.attributes.persona.secondary.osUpdateFrequency =
          "Unknown";

        const latestVersion = (latestOSVersions as any)[osName || "Android"];

        if (latestVersion && osVersion) {
          if (+osVersion >= +latestVersion) {
            osUpdateFrequency = "Frequent";
          }

          if (+osVersion === +latestVersion - 1) {
            osUpdateFrequency = "Moderate";
          }

          if (+osVersion < +latestVersion - 1) {
            osUpdateFrequency = "Rare";
          }
        }

        window.helium.attributes.persona.secondary.osUpdateFrequency =
          osUpdateFrequency;
        return window.helium.attributes.persona.secondary.osUpdateFrequency;
      },
    },
    {
      id: "browserUpdateFrequency",
      setup: () => {
        // Simulated latest browser versions (this should ideally be dynamically fetched from an API)
        const latestBrowserVersions = {
          Chrome: "116",
          Safari: "16",
          Firefox: "115",
        };

        const browserName = (window.helium.attributes.persona.primary.browser
          ?.name || "Chrome") as keyof typeof latestBrowserVersions;

        const browserVersion =
          window.helium.attributes.persona.primary.browser?.major;

        // Check if the browser is up to date
        let browserUpdateFrequency: typeof window.helium.attributes.persona.secondary.browserUpdateFrequency =
          "Unknown";

        const latestVersion = (latestBrowserVersions as any)[browserName];

        if (latestVersion && browserVersion) {
          if (+browserVersion >= +latestVersion) {
            browserUpdateFrequency = "Frequent";
          } else if (+browserVersion === +latestVersion - 1) {
            browserUpdateFrequency = "Moderate";
          } else if (+browserVersion < +latestVersion - 1) {
            browserUpdateFrequency = "Rare";
          }
        }

        window.helium.attributes.persona.secondary.browserUpdateFrequency =
          browserUpdateFrequency;
        return window.helium.attributes.persona.secondary
          .browserUpdateFrequency;
      },
    },

    {
      id: "cookiesSize",
      setup: () => {
        const cookies = document.cookie.split("; ").length;
        if (cookies > 50) {
          window.helium.attributes.persona.secondary.cookiesSize = "High";
        } else if (cookies > 20) {
          window.helium.attributes.persona.secondary.cookiesSize = "Moderate";
        } else {
          window.helium.attributes.persona.secondary.cookiesSize = "Low";
        }
        return window.helium.attributes.persona.secondary.cookiesSize;
      },
    },
  ];
